import { updateAllSellingPlans } from '@/cart/actions'
import {
  addLineItems,
  checkout,
  getCart,
  getCartError,
  updateCart,
  updateLineItem,
  waitForSettle
} from '@/cart/cart-store'
import type { Cart, CartLineItem } from '@/cart/types'
import type CtaButtonGroup from '@/components/buy-stack/bs-14/CtaButtonGroup'
import type QuantityInput from '@/components/buy-stack/bs-14/QuantityInput'
import type TierProgressIndicator from '@/components/buy-stack/bs-14/TierProgressIndicator'
import type { QuantityTier } from '@/components/buy-stack/bs-14/types'
import GiftWithPurchase from '@/gift-with-purchase/gift-with-purchase'
import { pushUrlParam, replaceUrlParam } from '@/utilities/replace-url-param'
import { effect, signal } from '@preact/signals'

import '@/components/buy-stack/bs-14/CtaButtonGroup'
import '@/components/buy-stack/bs-14/QuantityInput'
import '@/components/buy-stack/bs-14/TierProgressIndicator'
import '@/components/social-proof/SocialProofRegister'
import Experiment from '@/feature-flags/experiment'
import { getLocalizedStrings } from '@/localization/localization-store'
import withRootRoute from '@/localization/with-root-route'

/**
 * BS_14 "2-Step Tiered" Buy-stack component
 */
export default class BuyStack14 extends HTMLElement {
  public variantIds: number[] = []
  private _isSub = false
  private _sellingPlanId?: number
  private _cart: Cart | null = null
  private _cartError: string | null = null
  private _currentTier = [0, 0]
  private _pendingCheckout = false
  public tiers: QuantityTier[] = []
  public baseOnetimePrice = 0
  public baseSubPrice = 0
  public currency = 'USD'
  private _gwp: GiftWithPurchase | undefined
  public currentStep = signal(1)
  public tierPrices: string[] = []

  connectedCallback() {
    this.initState()
    this.setupListeners()
  }

  private get gwp(): GiftWithPurchase {
    this._gwp ??= new GiftWithPurchase({
      cart: {
        getCart,
        getCartError,
        updateCart,
        addLineItems,
        waitForSettle
      }
    })
    this._gwp.setOptions({
      variants: this.variantIds,
      giftId: Number(this.lowestTier?.gift_bundle?.split('/').pop()),
      minQuantity: this.lowestTier?.min_quantity
    })

    return this._gwp
  }

  private get tierIndex(): number {
    return this.getCurrentOrFirstAvailableTierIndex(this.isSub)
  }

  private set tierIndex(value: number) {
    this._currentTier[Number(this.isSub)] = value
    pushUrlParam('tier', String(value))
  }

  private get lowestTier(): QuantityTier | undefined {
    if (this.tiers.length === 0) return undefined
    return this.tiers.reduce((lowest, current) => {
      return current.min_quantity < lowest.min_quantity ? current : lowest
    }, this.tiers[0])
  }

  private get pendingCheckout(): boolean {
    return this._pendingCheckout
  }

  private set pendingCheckout(value: boolean) {
    this._pendingCheckout = value
    this.render()
  }

  public get allowCheckout(): boolean {
    return !!this.gwp?.allowCheckout
  }

  public get currentTier(): QuantityTier | undefined {
    return this.tiers[this.tierIndex]
  }

  public getPriceLabel(isSub: boolean): string {
    return this.tierPrices[this.handleGetQualifiedTier(isSub)] || ''
  }

  private get cart() {
    return this._cart
  }

  private set cart(value: Cart | null) {
    this._cart = value
    if (value) {
      this.render()
    }
  }

  private get cartError() {
    return this._cartError
  }

  private set cartError(value: string | null) {
    this._cartError = value
    if (value) {
      this.render()
    }
  }

  private get isSub() {
    return this._isSub
  }

  private set isSub(value: boolean) {
    this._isSub = value
    this.render()
    const container = this.querySelector('.selection-container')
    container?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
  }

  private get sellingPlanId(): number | undefined {
    return this._isSub ? this._sellingPlanId : undefined
  }

  private set sellingPlanId(value: number | undefined) {
    this._sellingPlanId = value
  }

  private get totalQuantity(): number {
    const cart = getCart()

    if (!cart) {
      return 0
    }

    return cart.items
      .filter((item: CartLineItem) => this.variantIds.includes(item.id))
      .reduce((sum, item) => sum + item.quantity, 0)
  }

  public selectTier(index: number) {
    const isValidIndex = index >= 0 && index < this.tiers.length

    if (!isValidIndex) {
      return
    }

    this.tierIndex = index
    replaceUrlParam('tier', String(index))
    this.render()
  }

  private getVariantIndex(variantId: number): number {
    if (!this.cart) {
      return -1
    }

    return this.cart.items.findIndex(i => i.id === variantId)
  }

  private getQuantity(variantId: number): number {
    if (!this.cart) {
      return 0
    }

    return this.cart.items
      .filter(i => i.id === variantId)
      .reduce((sum, item) => sum + item.quantity, 0)
  }

  private initState(): void {
    this._cart = getCart()
    this.variantIds = JSON.parse(this.getAttribute('variant-ids') ?? '[]') as number[]
    this._isSub = this.hasAttribute('subscription')
    this._sellingPlanId = Number(this.getAttribute('selling-plan-id')) || undefined
    this.tiers = JSON.parse(this.getAttribute('tiers') ?? '[]') as QuantityTier[]
    this.baseOnetimePrice = Number(this.getAttribute('base-onetime-price')) || 0
    this.baseSubPrice = Number(this.getAttribute('base-sub-price')) || 0
    const tierAttr = this.getAttribute('tier')
    if (tierAttr) {
      this.tierIndex = Number(tierAttr) || 0
    }
    this.gwp.listenToCart()
    this.currentStep.value = Number(this.dataset.step) || 1
    this.tierPrices = JSON.parse(this.getAttribute('tier-prices') ?? '[]') as string[]
    replaceUrlParam('step', String(this.currentStep.value))
  }

  private setupListeners() {
    window.addEventListener('popstate', async () => this.handlePopState())

    this.onchange = (e: InputEvent) => this.handleChange(e)
    this.addEventListener('increment', (e: CustomEvent<{ variantId: number }>) =>
      this.handleIncrement(e)
    )
    this.addEventListener('decrement', (e: CustomEvent<{ variantId: number }>) =>
      this.handleDecrement(e)
    )
    this.addEventListener('checkout', () => this.handleCheckout())
    this.addEventListener('click', (e: MouseEvent) => this.handleClick(e))
    // Wait for the next task before triggering render
    // This ensures that the component is fully initialized
    setTimeout(() => {
      effect(() => (this.cart = getCart()))
      effect(() => (this.cartError = getCartError()))
    })
  }

  private async handlePopState() {
    const step = new URLSearchParams(window.location.search).get('step')
    // Only set step if it's different. This ensures that hash-only changes are ignored
    if (step !== null && Number(step) !== this.currentStep.value) {
      await this.setStep(Number(step) || 1)
    }
  }

  private handleClick({ target }: MouseEvent): void {
    if (!(target instanceof HTMLElement)) {
      return
    }

    if (target?.dataset.action === 'next') {
      void this.setStep(2)
    }

    if (target?.dataset.action === 'back') {
      void this.setStep(1)
    }
  }

  public async setStep(step: number) {
    if (document.startViewTransition) {
      await document.startViewTransition?.().ready
    }

    window.scrollTo({ top: 0 })
    pushUrlParam('step', String(step))
    this.currentStep.value = step
  }

  private handleChange({ target: t }: InputEvent): void {
    if (t instanceof HTMLInputElement && t.name === 'is-subscription') {
      void this.toggleSubscription(t.checked)
    }

    if (t instanceof HTMLInputElement && t.name === 'tier') {
      void this.selectTier(Number(t.value))
    }
  }

  public async toggleSubscription(isSub: boolean) {
    this.isSub = isSub
    await updateAllSellingPlans(this.variantIds, isSub ? (this.sellingPlanId ?? null) : null)
  }

  private async handleSellingPlanChange(sellingPlanId: number | undefined) {
    this.sellingPlanId = sellingPlanId
    await updateAllSellingPlans(this.variantIds, sellingPlanId ?? null)
  }

  public async handleIncrement(e: CustomEvent<{ variantId: number }>) {
    await addLineItems([
      {
        id: e.detail.variantId,
        quantity: 1,
        selling_plan: this.sellingPlanId
      }
    ])
    await this.handleSellingPlanChange(this.sellingPlanId)
  }

  private async handleDecrement(e: CustomEvent<{ variantId: number }>) {
    const id = e.detail.variantId
    const index = this.getVariantIndex(id)

    if (index === -1) {
      return
    }

    const cartItem = this.cart?.items[index]
    if (!cartItem) return

    const currentQty = cartItem.quantity

    await updateLineItem({
      id: id.toString(),
      quantity: currentQty - 1
    })

    await this.handleSellingPlanChange(this.sellingPlanId)
  }

  private async handleCheckout() {
    if (!this.allowCheckout) {
      return
    }

    this.pendingCheckout = true
    const url = Experiment.getInstance().getFlag('precheckout_page')
      ? '/pages/precheckout'
      : '/checkout'
    const rootRoute = getLocalizedStrings().storeRootRoute
    await checkout(withRootRoute(url, rootRoute))
    setTimeout(() => {
      this.pendingCheckout = false
    }, 2000)
  }

  public get qualifiedTier(): number {
    return this.handleGetQualifiedTier(this.isSub)
  }

  private getCurrentOrFirstAvailableTierIndex(isSub: boolean): number {
    const idx = this._currentTier[Number(isSub)]

    const isAvailable = (tier?: QuantityTier) =>
      tier?.subscription_only === undefined || tier.subscription_only === isSub

    return isAvailable(this.tiers[idx]) ? idx : this.tiers.findIndex(isAvailable)
  }

  private handleGetQualifiedTier(isSub: boolean): number {
    let idx = this.getCurrentOrFirstAvailableTierIndex(isSub)
    let minQty = this.tiers[idx]?.min_quantity ?? 0
    const totalQty = this.totalQuantity

    for (let i = 0; i < this.tiers.length; i++) {
      const t = this.tiers[i]

      if (t.subscription_only !== undefined && t.subscription_only !== isSub) {
        continue
      }

      if (t.min_quantity > minQty && totalQty >= t.min_quantity) {
        minQty = t.min_quantity
        idx = i
      }
    }

    return idx
  }

  private render() {
    this.toggleAttribute('subscription', this.isSub)

    const inputs = this.querySelectorAll<QuantityInput>('quantity-input')
    inputs.forEach(el => (el.value = this.getQuantity(el.variantId)))

    const btnGroup = this.querySelector<CtaButtonGroup>('cta-button-group')

    if (btnGroup) {
      btnGroup.disabled = !this.allowCheckout
      btnGroup.minQuantity = this.currentTier?.min_quantity ?? 1
      btnGroup.loading = this.pendingCheckout || this.gwp.isPending
    }

    const progressIndicators =
      this.querySelectorAll<TierProgressIndicator>('tier-progress-indicator')
    progressIndicators.forEach(progressIndicator => {
      progressIndicator.totalQuantity = this.totalQuantity
    })



    const prettyBasePrice = '$' + this.baseOnetimePrice.toString()


    // Subscription price
    const subPrice = this.getPriceLabel(true)
    const subPriceLabels = this.querySelectorAll<HTMLElement>('.subscription-price')
    subPriceLabels.forEach(el => { el.textContent = subPrice })

    // Subscription Compare price
    const subComparePrice = prettyBasePrice
    const subComparePriceLabels = this.querySelectorAll<HTMLElement>('.subscription-compare-price')
    subComparePriceLabels.forEach(el => { el.textContent = subComparePrice })



    // Onetime price
    const otpPrice = this.getPriceLabel(false)
    const otpPriceLabels = this.querySelectorAll<HTMLElement>('.onetime-price')
    otpPriceLabels.forEach(el => { el.textContent = otpPrice })

    // Onetime Compare price
    //const part2 = otpPrice
    let otpComparePrice = '' 
    if ( prettyBasePrice != otpPrice ) otpComparePrice = '$' + this.baseOnetimePrice.toString()
    const otpComparePriceLabels = this.querySelectorAll<HTMLElement>('.onetime-compare-price')
    otpComparePriceLabels.forEach(el => { el.textContent = otpComparePrice })







    const giftRollTiers = this.querySelectorAll<HTMLElement>('.gift-roll [data-tier]')
    giftRollTiers.forEach((el, i) => {
      el.classList.toggle('opacity-0', i !== this.tierIndex)
    })

    this.dataset.step = String(this.currentStep.value)

    const tierRadios = this.querySelectorAll<HTMLInputElement>('input[name="tier"]')
    const tierIndex = this.tierIndex
    tierRadios.forEach((radio, i) => {
      radio.checked = i === tierIndex
      radio.disabled = !!this.tiers[i]?.subscription_only && !this.isSub
    })

    // cart error message
    const errorContainer = this.querySelector('#cart-error')
    const errorSpan = errorContainer?.getElementsByTagName('span')[0]
    if (errorContainer && errorSpan) {
      errorContainer.classList.toggle('opacity-0', !this.cartError)
      errorContainer.classList.toggle('max-h-0', !this.cartError)
      errorContainer.classList.toggle('max-h-12', !!this.cartError)
      errorContainer.classList.toggle('pointer-events-none', !this.cartError)

      if (this.cartError) {
        errorSpan.textContent = this.cartError
        errorSpan.scrollIntoView({ behavior: 'smooth', block: 'center' })
      } else {
        errorContainer.addEventListener('transitionend', () => {
          errorSpan.textContent = this.cartError
        })
      }
    }
  }
}

if (!customElements.get('buy-stack-14')) {
  customElements.define('buy-stack-14', BuyStack14)
}
